import {
    Box,
    Button,
    Card,
    CardActionArea,
    CardContent,
    CardHeader,
    IconButton,
    InputLabel,
    Stack,
    Switch,
    Tooltip,
    Typography,
} from '@mui/material';

import {
    RequestMethod,
    RequestStatus,
    ServerRequestState,
    useServerErrorEffect,
    useServerFetch,
    useServerSuccessEffect,
    useServerWrite,
} from 'dg-web-shared/lib/hooks/ServerStateHooks.ts';
import { ValidationData } from 'dg-web-shared/lib/forms/FormValidationHelpers.tsx';
import { ParkingaboUser } from './OperatorParkingaboUsersForm.tsx';
import { Localized } from 'dg-web-shared/lib/Localization.tsx';
import { OperatorAsyncLoadedSection } from '../app/components/OperatorAsyncLoadedSection.tsx';
import { BackendRequestErrorMessage } from 'dg-web-shared/common/components/material-ui/BackendRequestErrorMessage.tsx';
import {
    ParkingaboVehicle,
    VehicleLicensePlateType,
} from 'dg-web-shared/common/models/Vehicle.tsx';
import { VehicleTypeIcon } from 'dg-web-shared/common/components/material-ui/VehicleTypeIcon.tsx';
import { Outlet, useNavigate, useOutletContext } from 'react-router-dom';
import Alert from '@mui/material/Alert';
import {
    LastUpdateTimestampAndRefresh,
    LastUpdateTimestampAndRefreshAsyncLoadedSection,
} from '../ui/LastUpdateTimestampAndRefresh.tsx';
import { ResultLimitAlert } from '../shared-mui-components/ListMessages.tsx';
import { OperatorRoutedModalContent } from '../layout/components/OperatorRoutedModalContent.tsx';
import {
    DetailHeader,
    DetailTabs,
    HeaderBarWithRefreshButton,
    ParkingaboUserInfoTab,
    useParkingaboUserDetailOutletContext,
} from './OperatorParkingaboUsersDetail.tsx';
import { useQuery } from '../shared-mui-components/filter/OperatorFilterHelpers.tsx';
import { formatBadgeLabelByType } from 'product-shared/tenant/TenantVehicleIdentificationUtils';
import { CustomerTenantState } from 'product-shared/parkingabo/CustomerTenantState.ts';
import { useState } from 'react';
import { parkingaboUrl } from '../utils/UrlUtil.ts';
import {
    ConfirmationDialog,
    ConfirmationDialogVersions,
} from '../operator-logins/components/ConfirmationDialog.tsx';
import { useOperatorContext } from '../app/components/BaseLayoutAndData.tsx';
import InfoIcon from '@mui/icons-material/Info';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import {
    VehicleIdentification,
    ParkingaboCustomerPaymentMode,
} from 'product-shared/parkingabo/ParkingaboOperatingMode.tsx';
import { ParkingaboRole } from '../parkingabo-role/OperatorParkingaboRole.ts';

export interface ParkingaboVehicleSearchResult {
    results: ParkingaboVehicle[];
    eof: boolean;
}

export function OperatorParkingaboUsersVehicles() {
    const { parkingaboUser, refetchParkingaboUser } =
        useParkingaboUserDetailOutletContext();
    const { currentLogin } = useOperatorContext();
    const [vehiclesState, refetchList] = useServerFetch<
        ParkingaboVehicleSearchResult,
        { customerNr: string },
        null
    >(
        context => ({
            url: `/ui-api/operator-account/parkingabo/customer/${context.customerNr}/vehicles`,
        }),
        { customerNr: parkingaboUser.customerNr },
    );

    const query = useQuery();
    const navigate = useNavigate();

    const [enableConfirmation, setEnableConfirmation] = useState(false);
    const [disableConfirmation, setDisableConfirmation] = useState(false);
    const [updateExternalVehicleUrlTokenState, updateExternalVehicleUrlToken] =
        useServerWrite<
            {
                customerNr: string;
                activate: boolean;
            },
            null,
            ValidationData
        >(({ customerNr }) => ({
            url: `/ui-api/operator-account/parkingabo/customer/${customerNr}/external-vehicle-url-token`,
            method: RequestMethod.POST,
        }));

    useServerSuccessEffect(updateExternalVehicleUrlTokenState, () => {
        setEnableConfirmation(false);
        setDisableConfirmation(false);
        refetchParkingaboUser();
    });
    useServerErrorEffect(updateExternalVehicleUrlTokenState, () => {
        setEnableConfirmation(false);
        setDisableConfirmation(false);
    });

    const canEditExternalVehicle = currentLogin.permissions.userPermissionEdit;
    const showExternalVehicle =
        parkingaboUser.role.paymentMode !==
            ParkingaboCustomerPaymentMode.APP_PAYMENT &&
        parkingaboUser.state !== CustomerTenantState.ARCHIVED &&
        (canEditExternalVehicle ||
            parkingaboUser.externalVehicleUrlToken !== null);
    const isUpdatingExternalVehicle =
        updateExternalVehicleUrlTokenState.status === RequestStatus.PENDING;
    const active = parkingaboUser.externalVehicleUrlToken !== null;

    return (
        <OperatorRoutedModalContent
            header={<DetailHeader />}
            bar={
                <DetailTabs
                    parkingaboUserInfoTab={ParkingaboUserInfoTab.VEHICLES}
                    roleHasAppPayment={
                        parkingaboUser.role.paymentMode ===
                        ParkingaboCustomerPaymentMode.APP_PAYMENT
                    }
                />
            }
            style={{ padding: '0px 0px' }}
            body={
                <>
                    <HeaderBarWithRefreshButton
                        button={
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={() => navigate(`add?${query}`)}
                                disabled={
                                    parkingaboUser.state !==
                                        CustomerTenantState.ACTIVE ||
                                    parkingaboUser.needsProduct
                                }
                            >
                                <Localized
                                    de="Hinzufügen"
                                    fr="Ajouter"
                                    it="Aggiungi"
                                    en="Add"
                                />
                            </Button>
                        }
                        refreshButton={
                            <LastUpdateTimestampAndRefreshAsyncLoadedSection
                                requestState={vehiclesState}
                                onRefresh={refetchList}
                                render={(vehicles, timestamp) => (
                                    <LastUpdateTimestampAndRefresh
                                        resultsCount={vehicles.results.length}
                                        eof={vehicles.eof}
                                        onRefresh={refetchList}
                                        timestamp={timestamp}
                                    />
                                )}
                            />
                        }
                    />
                    <Box
                        sx={{
                            flex: '1 1 0',
                            display: 'flex',
                            flexDirection: 'column',
                            overflowY: 'auto',
                        }}
                    >
                        <VehicleCardSection
                            vehiclesState={vehiclesState}
                            parkingaboUser={parkingaboUser}
                            onGoToDetails={(vehicle: ParkingaboVehicle) =>
                                navigate(
                                    `${vehicle.customerTenantCarId}?${query}`,
                                )
                            }
                        />
                    </Box>

                    <UserVehiclesOutlet
                        context={{
                            parkingaboUser: parkingaboUser,
                            vehiclesState: vehiclesState,
                            refetchList: refetchList,
                            refetchParkingaboUser: refetchParkingaboUser,
                        }}
                    />

                    {showExternalVehicle && (
                        <>
                            <Box
                                sx={{
                                    padding: theme => theme.spacing(2, 3),
                                }}
                            >
                                <InputLabel variant="standard">
                                    <Localized
                                        de="Externer Zugriff"
                                        fr="Accès externe"
                                        it="Accesso esterno"
                                        en="External access"
                                    />
                                    <Tooltip
                                        title={
                                            <Localized
                                                de="Über den externen Zugriff können lediglich die Bezeichnung und das Kennzeichen der Fahrzeuge geändert werden. Es ist nicht möglich, zusätzliche Fahrzeuge hinzuzufügen."
                                                fr="L'accès externe permet uniquement de modifier la description et la plaque d'immatriculation des véhicules. Il n'est pas possible d'ajouter des véhicules supplémentaires."
                                                it="Tramite l'accesso esterno è possibile modificare unicamente la descrizione e la targa dei veicoli. Non è possibile aggiungere veicoli supplementari."
                                                en="Through the external access, you can only change the description and license plate of the vehicles. It is not possible to add additional vehicles."
                                            />
                                        }
                                        placement="right"
                                    >
                                        <InfoIcon
                                            sx={{
                                                verticalAlign: 'bottom',
                                                color: theme =>
                                                    theme.palette.blue.main,
                                                marginLeft: '4px',
                                            }}
                                        />
                                    </Tooltip>
                                </InputLabel>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        marginTop: -1,
                                        marginLeft: -1,
                                    }}
                                >
                                    <Stack
                                        direction="row"
                                        sx={{
                                            alignItems: 'center',
                                        }}
                                    >
                                        <Switch
                                            checked={active}
                                            disabled={
                                                isUpdatingExternalVehicle ||
                                                !canEditExternalVehicle
                                            }
                                            onChange={() => {
                                                if (active) {
                                                    setDisableConfirmation(
                                                        true,
                                                    );
                                                } else {
                                                    setEnableConfirmation(true);
                                                }
                                            }}
                                        />
                                        <Box
                                            sx={{
                                                color: theme =>
                                                    theme.palette.blue.main,
                                                fontSize: 14,
                                                fontWeight: 500,
                                            }}
                                        >
                                            {active ? (
                                                <ExternalVehiclesLink
                                                    url={getExternalVehiclesUrl(
                                                        parkingaboUser.externalVehicleUrlToken,
                                                    )}
                                                />
                                            ) : (
                                                <Localized
                                                    de="nicht aktiv"
                                                    fr="inactif"
                                                    it="non attivo"
                                                    en="not active"
                                                />
                                            )}
                                        </Box>
                                    </Stack>
                                </Box>

                                <EnableConfirmationModal
                                    open={enableConfirmation}
                                    disabled={isUpdatingExternalVehicle}
                                    onConfirm={() => {
                                        updateExternalVehicleUrlToken({
                                            customerNr:
                                                parkingaboUser.customerNr,
                                            activate: true,
                                        });
                                    }}
                                    onCancel={() => {
                                        setEnableConfirmation(false);
                                    }}
                                />
                                <DisableConfirmationModal
                                    open={disableConfirmation}
                                    disabled={isUpdatingExternalVehicle}
                                    onConfirm={() => {
                                        updateExternalVehicleUrlToken({
                                            customerNr:
                                                parkingaboUser.customerNr,
                                            activate: false,
                                        });
                                    }}
                                    onCancel={() => {
                                        setDisableConfirmation(false);
                                    }}
                                />
                            </Box>
                            <BackendRequestErrorMessage
                                requestState={
                                    updateExternalVehicleUrlTokenState
                                }
                            />
                        </>
                    )}
                </>
            }
        />
    );
}

function EnableConfirmationModal({
    open,
    disabled,
    onConfirm,
    onCancel,
}: {
    open: boolean;
    disabled: boolean;
    onConfirm: () => void;
    onCancel: () => void;
}) {
    return (
        <ConfirmationDialog
            open={open}
            title={
                <Localized
                    de="Aktivierung externen Zugriffs"
                    fr="Activation accès externe"
                    it="Attivazione accesso esterno"
                    en="External access activation"
                />
            }
            confirmLabel={
                <Localized
                    de="Aktivieren"
                    fr="Activer"
                    it="Attiva"
                    en="Activate"
                />
            }
            onConfirm={onConfirm}
            confirmDisabled={disabled}
            cancelLabel={
                <Localized
                    de="Abbrechen"
                    fr="Annuler"
                    it="Annulla"
                    en="Cancel"
                />
            }
            onCancel={onCancel}
            dialogVersion={ConfirmationDialogVersions.PRIMARY}
        >
            <>
                <p>
                    <Localized
                        de="Durch die Aktivierung des externen Zugriffs wird eine eindeutige URL-Adresse generiert, die Sie mit dem Kontoinhaber teilen können, um ihm Zugriff auf die Fahrzeugliste zu gewähren."
                        fr="En activant l'accès externe, une adresse URL unique sera générée à partager avec le titulaire du compte, afin de lui donner accès à la liste des véhicules."
                        it="Attivando l'accesso esterno verrà generato un indirizzo URL univoco da condividere con il titolare del conto, per dargli accesso alla lista dei veicoli."
                        en="By enabling external access, a unique URL address will be generated to share with the account holder, giving them access to the list of vehicles."
                    />
                </p>
                <p>
                    <Localized
                        de="Über den externen Zugriff können lediglich die Bezeichnung und das Kennzeichen der Fahrzeuge geändert werden. Es ist nicht möglich, zusätzliche Fahrzeuge hinzuzufügen."
                        fr="L'accès externe permet uniquement de modifier la description et la plaque d'immatriculation des véhicules. Il n'est pas possible d'ajouter des véhicules supplémentaires."
                        it="Tramite l'accesso esterno è possibile modificare unicamente la descrizione e la targa dei veicoli. Non è possibile aggiungere veicoli supplementari."
                        en="Through the external access, you can only change the description and license plate of the vehicles. It is not possible to add additional vehicles."
                    />
                </p>
            </>
        </ConfirmationDialog>
    );
}

function DisableConfirmationModal({
    open,
    disabled,
    onConfirm,
    onCancel,
}: {
    open: boolean;
    disabled: boolean;
    onConfirm: () => void;
    onCancel: () => void;
}) {
    return (
        <ConfirmationDialog
            open={open}
            title={
                <Localized
                    de="Deaktivierung externen Zugriffs"
                    fr="Désactivation accès externe"
                    it="Disattivazione accesso esterno"
                    en="External access deactivation"
                />
            }
            confirmLabel={
                <Localized
                    de="Deaktivieren"
                    fr="Désactiver"
                    it="Disattiva"
                    en="Deactivate"
                />
            }
            onConfirm={onConfirm}
            confirmDisabled={disabled}
            cancelLabel={
                <Localized
                    de="Abbrechen"
                    fr="Annuler"
                    it="Annulla"
                    en="Cancel"
                />
            }
            onCancel={onCancel}
            dialogVersion={ConfirmationDialogVersions.SOFT_WARNING}
        >
            <p>
                <Localized
                    de="Die URL-Adresse für den externen Zugriff wird deaktiviert; im Falle einer Reaktivierung wird eine neue Adresse generiert."
                    fr="L'adresse URL pour l'accès externe sera supprimée; en cas de réactivation, une nouvelle adresse sera générée."
                    it="L'indirizzo URL per l'accesso esterno verrà annullato; in caso di riattivazione verrà generato un nuovo indirizzo."
                    en="The URL address for external access will be cancelled; in the event of reactivation, a new address will be generated."
                />
            </p>
        </ConfirmationDialog>
    );
}

function ExternalVehiclesLink({ url }: { url: string }) {
    return (
        <Stack
            direction="row"
            sx={{
                alignItems: 'center',
            }}
        >
            <Box>{url}</Box>
            <Tooltip
                title={
                    <Localized
                        de="Link in Zwischenablage kopieren"
                        fr="Copier le lien dans le presse-papiers"
                        it="Copia il link negli appunti"
                        en="Copy link to clipboard"
                    />
                }
                placement="top"
            >
                <IconButton
                    size="small"
                    onClick={() => {
                        navigator.clipboard.writeText(url);
                    }}
                >
                    <ContentCopyIcon fontSize="small" />
                </IconButton>
            </Tooltip>
        </Stack>
    );
}

function getExternalVehiclesUrl(externalVehicleUrlToken: string | null) {
    return parkingaboUrl() + '/v/' + externalVehicleUrlToken;
}

export function useUserVehiclesOutlet() {
    return useOutletContext<UserVehiclesOutletContext>();
}

function UserVehiclesOutlet({
    context,
}: {
    context: UserVehiclesOutletContext;
}) {
    return <Outlet context={context} />;
}

type UserVehiclesOutletContext = {
    parkingaboUser: ParkingaboUser;
    vehiclesState: ServerRequestState<ParkingaboVehicleSearchResult, null>;
    refetchList: () => void;
    refetchParkingaboUser: () => void;
};

function getVehicleHeader(vehicle: ParkingaboVehicle, role: ParkingaboRole) {
    const formattedLp = vehicle.licensePlateNr ? (
        `${vehicle.licensePlateNr} (${vehicle.country || ' - '})`
    ) : (
        <Localized
            de="KENNZEICHEN FEHLT"
            fr="PLAQUE MANQUANTE"
            it="TARGA MANCANTE"
            en="MISSING LP"
        />
    );
    if (
        role.vehicleIdentification.includes(VehicleIdentification.LICENSE_PLATE)
    ) {
        return formattedLp;
    }
    if (role.vehicleIdentification.includes(VehicleIdentification.BADGE)) {
        return formatBadgeLabelByType(vehicle.badgeLabelNr, vehicle.badgeType);
    }
    if (role.vehicleIdentification.includes(VehicleIdentification.QR)) {
        return vehicle.identificationQrCodeId;
    }
    return '';
}

function getVehicleFooter(vehicle: ParkingaboVehicle, role: ParkingaboRole) {
    const formattedLp = `${vehicle.licensePlateNr} (${
        vehicle.country || ' - '
    })`;
    if (
        role.vehicleIdentification.includes(VehicleIdentification.QR) &&
        role.vehicleIdentification.includes(VehicleIdentification.LICENSE_PLATE)
    ) {
        return vehicle.identificationQrCodeId;
    }
    if (
        role.vehicleIdentification.includes(VehicleIdentification.BADGE) &&
        role.vehicleIdentification.includes(VehicleIdentification.LICENSE_PLATE)
    ) {
        return formatBadgeLabelByType(vehicle.badgeLabelNr, vehicle.badgeType);
    }
    if (
        role.vehicleIdentification.includes(VehicleIdentification.BADGE) ||
        role.vehicleIdentification.includes(VehicleIdentification.QR)
    ) {
        return vehicle.licensePlateNr !== null ? formattedLp : '';
    }
    return '';
}

function VehicleCardSection({
    vehiclesState,
    parkingaboUser,
    onGoToDetails,
}: {
    vehiclesState: ServerRequestState<ParkingaboVehicleSearchResult, null>;
    parkingaboUser: ParkingaboUser;
    onGoToDetails: (vehicle: ParkingaboVehicle) => void;
}) {
    return (
        <OperatorAsyncLoadedSection
            requestState={vehiclesState}
            render={vehicles => {
                if (vehicles.results.length === 0) {
                    return (
                        <>
                            {parkingaboUser.needsProduct ? (
                                <Alert severity="warning">
                                    <Localized
                                        de="Es ist nicht möglich, Fahrzeuge hinzuzufügen, da noch kein Produkt registriert wurde."
                                        fr="Il n'est pas possible d'ajouter des véhicules, car aucun produit n'a encore été enregistré."
                                        it="Non è possibile aggiungere veicoli, poiché non è stato ancora registrato alcun prodotto."
                                        en="It is not possible to add vehicles, as no product has yet been registered."
                                    />
                                </Alert>
                            ) : (
                                <Alert severity="info">
                                    <Localized
                                        de="Dieser Benutzer hat keine Fahrzeuge."
                                        fr="Cet utilisateur n'a pas de véhicules."
                                        it="Questo utente non ha veicoli."
                                        en="This user has no vehicles."
                                    />
                                </Alert>
                            )}
                        </>
                    );
                }

                return (
                    <>
                        <Box
                            sx={{
                                flex: '0 1 auto',
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: '22px',
                                padding: 3,
                            }}
                        >
                            {vehicles.results.map(vehicle => (
                                <Card
                                    key={vehicle.customerTenantCarId}
                                    sx={{
                                        width: 200,
                                    }}
                                    elevation={0}
                                    onClick={() => onGoToDetails(vehicle)}
                                >
                                    <CardActionArea
                                        sx={{
                                            height: '100%',
                                        }}
                                        component="div"
                                    >
                                        <CardHeader
                                            title={getVehicleHeader(
                                                vehicle,
                                                parkingaboUser.role,
                                            )}
                                            sx={{
                                                paddingBottom: 0,
                                                textAlign: 'center',
                                                '& .MuiCardHeader-title': {
                                                    color: theme =>
                                                        !vehicle.licensePlateNr &&
                                                        parkingaboUser.role?.vehicleIdentification.includes(
                                                            VehicleIdentification.LICENSE_PLATE,
                                                        )
                                                            ? theme.palette
                                                                  .error.dark
                                                            : theme.palette.blue
                                                                  .main,
                                                },
                                            }}
                                        />
                                        <CardContent
                                            sx={{
                                                paddingTop: 0,
                                                textAlign: 'center',
                                                color: theme =>
                                                    theme.palette.blue.main,
                                            }}
                                        >
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap',
                                                }}
                                            >
                                                {vehicle.description}
                                            </Typography>
                                            <Box sx={{ textAlign: 'center' }}>
                                                <VehicleTypeIcon
                                                    sx={{
                                                        fontSize: 50,
                                                    }}
                                                    type={
                                                        vehicle.type ||
                                                        VehicleLicensePlateType.CAR
                                                    }
                                                />
                                            </Box>
                                            <Typography variant="body2">
                                                {getVehicleFooter(
                                                    vehicle,
                                                    parkingaboUser.role,
                                                )}
                                            </Typography>
                                        </CardContent>
                                    </CardActionArea>
                                </Card>
                            ))}
                        </Box>
                        <ResultLimitAlert
                            eof={vehicles.eof}
                            numberOfResults={vehicles.results.length}
                            hasExport={false}
                        />
                    </>
                );
            }}
        />
    );
}
